import { saveBlobAs, type AuthedFile } from "@/lib/api";

/**
 * Where a problem set PDF goes once it is made (`EX-PDF02`): saved as a file,
 * or opened in a new tab with the print dialog raised over it.
 *
 * The tab is opened on the press itself, before the PDF exists, because a
 * browser only lets a page open a tab inside the user's own gesture; it says
 * the PDF is being made until the file arrives. When the browser blocked the
 * tab anyway, the PDF prints from a hidden frame in this page instead.
 */

export type PdfAction = "download" | "print";

/** How the print went out: in its own tab, or from a frame because the tab was blocked. */
export type PdfDelivery = "saved" | "tab" | "frame";

/** How long a printed PDF's address stays valid: the tab reads it again on a reload. */
const PRINT_URL_LIFETIME_MS = 10 * 60 * 1000;
const PRINT_POLL_MS = 250;
const PRINT_POLL_LIMIT_MS = 20_000;

/** Opens the print tab inside the press, with a line that says what is coming. */
export function openPrintTab(waiting: string): Window | null {
  const tab = window.open("", "_blank");
  if (!tab) return null;
  try {
    tab.document.title = waiting;
    tab.document.body.textContent = waiting;
    tab.document.body.style.fontFamily = "Inter, system-ui, sans-serif";
    tab.document.body.style.padding = "24px";
  } catch {
    // A tab the browser handed back without a document still takes a location.
  }
  return tab;
}

/** Closes a print tab whose PDF never came, so a refusal leaves no blank tab behind. */
export function closePrintTab(tab: Window | null): void {
  if (tab && !tab.closed) tab.close();
}

export function deliverPdf(file: AuthedFile, action: PdfAction, tab: Window | null): PdfDelivery {
  if (action === "download") {
    saveBlobAs(file.blob, file.filename);
    return "saved";
  }
  const url = URL.createObjectURL(file.blob);
  window.setTimeout(() => URL.revokeObjectURL(url), PRINT_URL_LIFETIME_MS);
  if (tab && !tab.closed) {
    tab.location.href = url;
    printWhenLoaded(tab);
    return "tab";
  }
  printFromFrame(url);
  return "frame";
}

/** The tab is ours (a blob address on this origin), so its print can be called once the PDF has loaded. */
function printWhenLoaded(tab: Window): void {
  const started = Date.now();
  const timer = window.setInterval(() => {
    let ready = false;
    try {
      ready = tab.location.protocol === "blob:" && tab.document.readyState === "complete";
    } catch {
      ready = false;
    }
    if (ready) {
      window.clearInterval(timer);
      tab.focus();
      tab.print();
    } else if (tab.closed || Date.now() - started > PRINT_POLL_LIMIT_MS) {
      window.clearInterval(timer);
    }
  }, PRINT_POLL_MS);
}

function printFromFrame(url: string): void {
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.setAttribute("aria-hidden", "true");
  frame.src = url;
  frame.onload = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), PRINT_URL_LIFETIME_MS);
  };
  document.body.appendChild(frame);
}
